/**
 * Sheet — 공통 bottom sheet 셸 (portal + 드래그 dismiss + scroll lock).
 *
 * 사용 시나리오:
 *  - 컨텐츠 시트 (배송지 변경 등): 페이지 안에서 `<Sheet open onClose>...</Sheet>` 로 호출.
 *  - 라우트 시트 (배송지 추가/수정 등): Layout 이 `presentation: 'sheet'` 라우트를
 *    감지해 outlet 을 이 컴포넌트로 감쌈.
 *
 * 핵심 동작:
 *  - createPortal(document.body) — 부모의 `transform`/`will-change` (routeMotion 등)
 *    가 `position: fixed` containing block 을 가두는 문제 회피.
 *  - 핸들 영역 pointerDown → framer-motion drag (axis y).
 *    100px 이상 끌거나 빠른 swipe 면 onClose 호출. 그 외 spring back.
 *  - 오른쪽 위 X — 닫는 길이 손가락으로 끄는 것뿐이면 안 되기 때문이다
 *    (2026-08-25). 핸들은 대비 1.41:1 에 잡는 영역이 16px 이라 있는 줄 모르면
 *    못 쓰고, 스크린리더·키보드에는 애초에 잡을 것이 없다.
 *  - 백드롭 클릭 / ESC 도 onClose. dismissible=false 면 모두 비활성.
 *  - hardware/browser back-press → useDialogBackBinding 내장(bindBack, default on)
 *    으로 닫힘. 라우트 시트(Layout)만 bindBack=false — back = 라우트 pop 이 닫힘.
 *  - body + main 스크롤러 lock — 단순 body.overflow 만으로는 본문이 .main 안에서
 *    스크롤되므로 둘 다 잠금.
 *  - mounted state 로 exit 애니메이션 동안 DOM 유지. open=false 가 들어와도
 *    300ms 동안 unmount 지연.
 */
import { useEffect, useRef, useState, type ReactNode, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { motion, useMotionValue } from 'framer-motion';
import { useDialogBackBinding } from '../../navigation/useDialogBackBinding';
import { useTranslation } from 'react-i18next';
import Icon from '../primitives/Icon';
import styles from './Sheet.module.css';

interface SheetProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  ariaLabel?: string;
  /** 백드롭/드래그/ESC 로 닫을 수 있는지. default true. */
  dismissible?: boolean;
  /** 핸들 노출 여부. default true. dismissible=false 면 무시. */
  showHandle?: boolean;
  /** 시트 본문 스타일 variant. default 'auto' (max-height 80vh). */
  variant?: 'auto' | 'tall';
  /**
   * hardware back / browser back(popstate, iOS swipe-back) 으로 닫히게 하는
   * dialog 바인딩. default true — 바텀시트는 항상 뒤로가기로 닫혀야 한다.
   * false 는 **라우트 시트**(Layout 의 presentation:'sheet') 전용 — 시트 자체가
   * history entry 라 back = 라우트 pop 이 곧 닫힘이고, 여기서 또 바인딩하면
   * sentinel 정리의 navigate(-1) 와 겹쳐 이중 pop 이 된다.
   */
  bindBack?: boolean;
  /** dialog stack 디버그/추적용 kind. default 'sheet'. */
  backKind?: string;
}

const DRAG_DISMISS_OFFSET = 100;
const DRAG_DISMISS_VELOCITY = 0.5; // px/ms

export default function Sheet({
  open,
  onClose,
  children,
  ariaLabel,
  dismissible = true,
  showHandle = true,
  variant = 'auto',
  bindBack = true,
  backKind = 'sheet',
}: SheetProps) {
  // Back-press → 닫기. dismissible=false 시트는 닫는 대신 back 을 흡수한다
  // (blockClose) — 시트가 떠 있는데 밑의 화면만 네비게이션되면 안 되므로.
  useDialogBackBinding({
    isOpen: open && bindBack,
    close: onClose,
    kind: backKind,
    blockClose: !dismissible,
  });

  const { t } = useTranslation('common');
  // 드래그용 motion value — open/close 애니메이션과 분리해 충돌 방지.
  // open/close 는 outer motion.div 의 y % 변환으로 처리, drag 는 inner 의 dragY px.
  const dragY = useMotionValue(0);
  const startYRef = useRef(0);
  const startTimeRef = useRef(0);
  const draggingRef = useRef(false);
  // exit 애니메이션 동안 DOM 유지하기 위한 mounted state.
  // open=true → 즉시 mounted=true. open=false → 280ms 후 unmount.
  const [mounted, setMounted] = useState(open);
  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }
    if (!mounted) return;
    const t = window.setTimeout(() => setMounted(false), 300);
    return () => window.clearTimeout(t);
  }, [open, mounted]);

  // ESC + scroll lock — body 만 잠그면 부족 (Layout 의 .main 과 .routeMotion 이
  // 별도로 overflow-y: auto). 실제 스크롤러를 모두 잠가야 본문이 안 밀림.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissible) onClose();
    };
    document.addEventListener('keydown', onKey);

    const body = document.body;
    const lockTargets: HTMLElement[] = [body];
    body.querySelectorAll<HTMLElement>('main, [class*="routeMotion"], [class*="overlayMain"]').forEach((el) => {
      lockTargets.push(el);
    });
    const prevOverflows = lockTargets.map((el) => el.style.overflow);
    lockTargets.forEach((el) => {
      el.style.overflow = 'hidden';
    });

    // Astryx Carousel 의 좌우 화살표는 popover 로 뜬다 = 브라우저 **top layer** 라
    // z-index 를 아무리 올려도 시트 위에 그대로 떠 있는다. (실제로 홈 캐러셀의 '›' 가
    // 시트 목록 위에 겹쳐 보였다.) 열려 있는 동안만 감추고 닫을 때 되돌린다.
    const floatingNavs = [...body.querySelectorAll<HTMLElement>('[class*="astryx-carousel"] [popover]')];
    const prevVisibilities = floatingNavs.map((el) => el.style.visibility);
    floatingNavs.forEach((el) => {
      el.style.visibility = 'hidden';
    });

    return () => {
      document.removeEventListener('keydown', onKey);
      lockTargets.forEach((el, i) => {
        el.style.overflow = prevOverflows[i] ?? '';
      });
      floatingNavs.forEach((el, i) => {
        el.style.visibility = prevVisibilities[i] ?? '';
      });
    };
  }, [open, dismissible, onClose]);

  const onHandlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dismissible) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    startYRef.current = e.clientY;
    startTimeRef.current = performance.now();
    draggingRef.current = true;
    dragY.set(0);
  };

  const onHandlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    const dy = e.clientY - startYRef.current;
    // 위로 끌면 거의 안 움직임 (탄성), 아래로는 1:1
    dragY.set(dy > 0 ? dy : dy * 0.2);
  };

  const onHandlePointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    const dy = e.clientY - startYRef.current;
    const dt = performance.now() - startTimeRef.current;
    const velocity = dt > 0 ? dy / dt : 0;
    if (dy > DRAG_DISMISS_OFFSET || velocity > DRAG_DISMISS_VELOCITY) {
      onClose();
    } else {
      // 스프링 백 — animate to 0
      void animateValueToZero(dragY);
    }
  };

  // 닫힐 때 dragY 잔여값 초기화 (다음 open 시 깔끔하게).
  useEffect(() => {
    if (!open) dragY.set(0);
  }, [open, dragY]);

  if (!mounted) return null;

  return createPortal(
    <div
      className={styles.root}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
    >
      <motion.div
        className={styles.backdrop}
        initial={{ opacity: 0 }}
        animate={{ opacity: open ? 1 : 0 }}
        transition={{ duration: 0.2 }}
        onClick={dismissible ? onClose : undefined}
      />
      <motion.div
        className={`${styles.sheetSlot} ${variant === 'tall' ? styles.sheetSlotTall : ''}`}
        initial={{ y: '100%' }}
        animate={{ y: open ? 0 : '100%' }}
        transition={{ type: 'tween', duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
      >
        <motion.div className={styles.sheet} style={{ y: dragY }}>
          {/* 닫기 — 핸들과 별개로 늘 손에 잡히는 문. dismissible=false 면
              닫을 수 없어야 하는 자리이므로 함께 사라진다. */}
          {dismissible && (
            <button
              type="button"
              className={styles.closeBtn}
              aria-label={t('action.close')}
              onClick={onClose}
            >
              <Icon name="Close" size="sm" />
            </button>
          )}
          {showHandle && dismissible && (
            <div
              className={styles.handleArea}
              onPointerDown={onHandlePointerDown}
              onPointerMove={onHandlePointerMove}
              onPointerUp={onHandlePointerUp}
              onPointerCancel={onHandlePointerUp}
              style={{ touchAction: 'none' }}
              aria-hidden
            >
              <div className={styles.handle} />
            </div>
          )}
          <div className={styles.content}>{children}</div>
        </motion.div>
      </motion.div>
    </div>,
    document.body,
  );
}

/** dragY 를 0 으로 부드럽게 spring back. */
function animateValueToZero(value: ReturnType<typeof useMotionValue<number>>) {
  const start = value.get();
  if (Math.abs(start) < 0.5) {
    value.set(0);
    return;
  }
  const startTime = performance.now();
  const duration = 200;
  const step = () => {
    const t = Math.min(1, (performance.now() - startTime) / duration);
    const eased = 1 - Math.pow(1 - t, 3);
    value.set(start * (1 - eased));
    if (t < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}
